/**
 * Palettes du mode nuit (dev) — aligner MUI / Ant Design / CSS (--dnm-*)
 * sur les mêmes valeurs que `src/styles/dev-night-mode-palettes.css`.
 */
export const DEV_NIGHT_PALETTE_STORAGE_KEY = 'rintintin:dev:nightPalette';

export const DEV_NIGHT_PALETTE_IDS = ['default', 'warm', 'ocean', 'contrast'] as const;
export type DevNightPaletteId = (typeof DEV_NIGHT_PALETTE_IDS)[number];

export function parseDevNightPaletteId(raw: string | null | undefined): DevNightPaletteId {
  if (raw === 'warm' || raw === 'ocean' || raw === 'contrast' || raw === 'default') return raw;
  return 'default';
}

/** Jetons MUI sombre (createDarkAppTheme) — copie des --dnm-* par palette. */
export type DevNightDarkTokens = {
  canvas: string;
  surfaceElevated: string;
  brandPrimary: string;
  brandPrimaryHover: string;
  brandGold: string;
  textPrimary: string;
  textSecondary: string;
  divider: string;
};

export const DEV_NIGHT_DARK_MUI: Record<DevNightPaletteId, DevNightDarkTokens> = {
  default: {
    canvas: '#121418',
    surfaceElevated: '#22262e',
    brandPrimary: '#c9a088',
    brandPrimaryHover: '#ddb69e',
    brandGold: '#e6c46a',
    textPrimary: '#f1f5f9',
    textSecondary: '#94a3b8',
    divider: 'rgba(148, 163, 184, 0.16)',
  },
  warm: {
    canvas: '#151311',
    surfaceElevated: '#252018',
    brandPrimary: '#d4a574',
    brandPrimaryHover: '#e8bc8c',
    brandGold: '#e8c078',
    textPrimary: '#f5f0e8',
    textSecondary: '#b9a896',
    divider: 'rgba(212, 165, 116, 0.18)',
  },
  ocean: {
    canvas: '#0f172a',
    surfaceElevated: '#1e293b',
    brandPrimary: '#94c5d8',
    brandPrimaryHover: '#b8dce8',
    brandGold: '#7dd3fc',
    textPrimary: '#f1f5f9',
    textSecondary: '#94a3b8',
    divider: 'rgba(148, 163, 184, 0.2)',
  },
  contrast: {
    canvas: '#09090b',
    surfaceElevated: '#18181b',
    brandPrimary: '#e4b896',
    brandPrimaryHover: '#f0cdb0',
    brandGold: '#fde68a',
    textPrimary: '#fafafa',
    textSecondary: '#d4d4d8',
    divider: 'rgba(250, 250, 250, 0.14)',
  },
};

export type AntdDarkSurfaceTokens = {
  colorPrimary: string;
  colorInfo: string;
  colorLink: string;
  colorBgBase: string;
  colorBgContainer: string;
  colorBgElevated: string;
  colorBorder: string;
  colorText: string;
  colorTextSecondary: string;
};

const ANT_SURFACES: Record<
  DevNightPaletteId,
  { base: string; container: string; elevated: string; border: string }
> = {
  default: {
    base: '#121418',
    container: '#1a1d24',
    elevated: '#22262e',
    border: 'rgba(148, 163, 184, 0.22)',
  },
  warm: {
    base: '#151311',
    container: '#1c1914',
    elevated: '#252018',
    border: 'rgba(212, 165, 116, 0.22)',
  },
  ocean: {
    base: '#0f172a',
    container: '#172033',
    elevated: '#1e293b',
    border: 'rgba(148, 163, 184, 0.24)',
  },
  contrast: {
    base: '#09090b',
    container: '#121214',
    elevated: '#18181b',
    border: 'rgba(250, 250, 250, 0.18)',
  },
};

/** Jetons Ant Design (token) pour mode sombre — alignés sur chaque palette. */
export function getAntdDarkPaletteTokens(id: DevNightPaletteId): AntdDarkSurfaceTokens {
  const t = DEV_NIGHT_DARK_MUI[id];
  const s = ANT_SURFACES[id];
  return {
    colorPrimary: t.brandPrimary,
    colorInfo: t.brandPrimary,
    colorLink: t.brandPrimaryHover,
    colorBgBase: s.base,
    colorBgContainer: s.container,
    colorBgElevated: s.elevated,
    colorBorder: s.border,
    colorText: t.textPrimary,
    colorTextSecondary: t.textSecondary,
  };
}

export const DEV_NIGHT_PALETTE_META: Record<
  DevNightPaletteId,
  { label: string; description: string; swatches: [string, string, string] }
> = {
  default: {
    label: 'Graphite',
    description: 'Neutre slate, référence actuelle.',
    swatches: ['#121418', '#22262e', '#c9a088'],
  },
  warm: {
    label: 'Ambre',
    description: 'Fonds bruns chauds, accents dorés.',
    swatches: ['#151311', '#252018', '#d4a574'],
  },
  ocean: {
    label: 'Océan',
    description: 'Bleus ardoise, accents froids.',
    swatches: ['#0f172a', '#1e293b', '#94c5d8'],
  },
  contrast: {
    label: 'Contraste',
    description: 'Noirs profonds, texte très lisible.',
    swatches: ['#09090b', '#18181b', '#e4b896'],
  },
};
